import React from "react";

import { faTrashCan } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faCircleCheck, faCirclePlus, faSparkles } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, Icon, Text, Tooltip } from "@performyard/py-components";

import ExpandableText from "../../../components/ExpandableText";

import type { CompetencyItemStatus, CompetencyLevel, CompetencySuggestion, EditableCompetencyFields } from "../types";

export const INFERRED_BY_AI_TOOLTIP = "Inferred by AI";

export const CompanyWideChip: React.FC = () => (
	<span className="shrink-0 rounded-full bg-primary-1 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-primary-7">
		Company wide
	</span>
);

export const hasInferredLevels = (
	suggestions: CompetencySuggestion[],
	editedFields: Record<string, Partial<EditableCompetencyFields>>
): boolean =>
	suggestions.some((suggestion) =>
		(editedFields[suggestion.id]?.levels ?? suggestion.levels).some((level) => level.source === "inferred")
	);

const LevelChip: React.FC<{ level: CompetencyLevel }> = ({ level }) => (
	<span className="inline-flex items-center gap-1 rounded-full bg-primary-1 px-2 py-0.5 text-xs font-medium text-primary-7">
		{level.source === "inferred" && <Icon icon={faSparkles} color="primary-7" size={12} />}
		{level.name}
	</span>
);

export const InferredLevelsLegend: React.FC = () => (
	<div className="flex items-center gap-1.5">
		<Icon icon={faSparkles} color="neutral-6" size={12} />
		<Text size="caption" color="neutral-6">
			Marks levels inferred by AI — the document didn&apos;t define them.
		</Text>
	</div>
);

export const CompetencyPreviewCard: React.FC<{
	name: string;
	description: string;
	levels: CompetencyLevel[];
	itemStatus: CompetencyItemStatus;
	itemError?: string;
	saving: boolean;
	isCompanyWide?: boolean;
	onCreate: () => void;
	onRemove: () => void;
}> = ({ name, description, levels, itemStatus, itemError, saving, isCompanyWide, onCreate, onRemove }) => {
	return (
		<div className="box-border flex max-w-full min-w-0 flex-col gap-2 rounded-md border border-solid border-neutral-3 bg-white p-3">
			<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
				{name}
			</Text>
			{isCompanyWide && (
				<div className="flex">
					<CompanyWideChip />
				</div>
			)}
			<ExpandableText content={description} collapseHeight={32} />
			<div className="flex flex-wrap gap-1">
				{levels.map((level) =>
					level.source === "inferred" ? (
						<Tooltip key={level.rank} content={INFERRED_BY_AI_TOOLTIP}>
							<LevelChip level={level} />
						</Tooltip>
					) : (
						<LevelChip key={level.rank} level={level} />
					)
				)}
			</div>
			{itemError && (
				<Text size="caption" color="red-6">
					{itemError}
				</Text>
			)}
			{/* Per-card footer */}
			<div className="flex items-center justify-between pt-1">
				{itemStatus === "created" ? (
					<span />
				) : (
					<ButtonIcon
						icon={faTrashCan}
						onClick={onRemove}
						variant="standard"
						color="neutral"
						shade={5}
						size={16}
						aria-label="Remove"
						disabled={itemStatus === "creating" || saving}
					/>
				)}
				{itemStatus === "created" ? (
					<span className="flex items-center gap-1 py-1">
						<Icon icon={faCircleCheck} color="green-6" size={16} />
						<Text size="paragraph-3" weight="semi-bold" color="green-6">
							Competency Created
						</Text>
					</span>
				) : (
					<ButtonStandard
						variant="text"
						color="primary"
						size="medium"
						iconLeft={faCirclePlus}
						onClick={onCreate}
						disabled={itemStatus === "creating" || saving}
						dangerouslySetClassName="!w-auto !h-auto border-0 py-1"
					>
						{itemStatus === "creating" ? "Creating…" : "Create Competency"}
					</ButtonStandard>
				)}
			</div>
		</div>
	);
};
